// Stencil
import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Method,
  Prop,
  State,
  getAssetPath,
  h
} from "@stencil/core";

// Other Libraries
import { getIconPath, MercuryBundles } from "@genexus/mercury";

// Custom Imports
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";
import { formatDate } from "../../../common/helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/edit",
  "components/icon",
  "components/radio-group",
  "components/tabular-grid",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

const GEMINI_TOOLS_ADD_ICON = getIconPath({
  category: "gemini-tools",
  name: "add-circle",
  colorType: "primary"
});
@Component({
  tag: "gx-ide-team-dev-update-partial-selection",
  styleUrl: "update-partial-selection.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-update-partial-selection"]
})
export class GxIdeTeamDevUpdatePartialSelection {
  #_componentLocale: any;
  #renderedFirstTime = false;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/team-dev-update-partial-selection/shortcuts.json`
  );

  @Element() el: HTMLGxIdeTeamDevUpdatePartialSelectionElement;

  #chGridEl!: HTMLChTabularGridElement;

  /**
   * Selected objects in objects table
   */
  @State() selectedObjectsIds: string[] = [];

  /**
   * Callback invoked to return a list of new objects to populate objects table.
   */
  @Prop() readonly addCallback!: AddCallback;

  /**
   * Callback invoked when the user wishes to cancel the selection of objects.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   *Callback invoked when confirm action is executed passing the selected objects ids.
   */
  @Prop() readonly confirmCallback!: ConfirmCallback;

  /**
   * Current objects selection in the grid
   */
  @Prop({ mutable: true }) selection: ObjectData[] = [];

  /**
   * Emitted once just after the component is fully loaded and the first render() occurs.
   */
  @Event() componentDidLoadEvent: EventEmitter<boolean>;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  componentDidLoad() {
    this.#listenChanges();
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
  }
  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.componentDidLoadEvent.emit(true);
  }

  /**
   * Validate necessary data input
   */
  @Method()
  async validate(): Promise<boolean> {
    const isValid = true;
    return isValid;
  }

  #cancelHandler = (): void => {
    this.cancelCallback();
  };

  #confirmHandler = (): void => {
    if (this.confirmCallback) {
      const allRowsIds: string[] = [];
      this.selection.forEach(row => {
        allRowsIds.push(row.id);
      });

      this.confirmCallback(allRowsIds);
    }
  };

  #getObjectsHandler = (): void => {
    this.addCallback().then((items: ObjectData[]) => {
      if (items?.length > 0) {
        this.selection = [...this.selection, ...items];
        this.selectedObjectsIds = [];
      }
    });
  };

  #listenChanges = (): void => {
    this.#chGridEl.addEventListener("selectionChanged", (ev: any): void => {
      this.selectedObjectsIds = ev.detail.rowsId;
    });
  };

  #removeAllHandler = (): void => {
    this.selection = [];
  };

  #removeSelectedObjectsHandler = (): void => {
    const selection = this.selection.filter(
      item => !this.selectedObjectsIds.includes(item.id)
    );
    this.selection = selection;
    this.selectedObjectsIds = [];
  };

  #renderObjects = (): Element => {
    return (
      <ch-tabular-grid
        allowColumnReorder={false}
        columnResizeMode="single"
        class="tabular-grid tabular-grid-main"
        row-selection-mode="multiple"
        keyboardNavigationMode="focus"
        ref={(el: HTMLChTabularGridElement) => (this.#chGridEl = el)}
        part="ch-grid-objects"
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            columnName=""
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.minContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            columnName={this.#_componentLocale.tableHead.name}
            settingable={false}
            sortable={false}
            size="1fr"
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            columnName={this.#_componentLocale.tableHead.type}
            settingable={false}
            sortable={false}
            size="minmax(120px,auto)"
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            columnName={this.#_componentLocale.tableHead.description}
            settingable={false}
            sortable={false}
            size="minmax(120px,auto)"
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            columnName={this.#_componentLocale.tableHead.modifiedOn}
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>
        {this.selection.map((obj: ObjectData) => (
          <ch-tabular-grid-row
            rowid={obj.id}
            key={obj.id}
            class="tabular-grid-row"
          >
            <ch-tabular-grid-cell class="tabular-grid-cell">
              <ch-image src={obj.typeIcon} class="icon-s"></ch-image>
            </ch-tabular-grid-cell>
            <ch-tabular-grid-cell class="tabular-grid-cell">
              {obj.name}
            </ch-tabular-grid-cell>
            <ch-tabular-grid-cell class="tabular-grid-cell">
              {obj.type}
            </ch-tabular-grid-cell>
            <ch-tabular-grid-cell class="tabular-grid-cell">
              {obj.description}
            </ch-tabular-grid-cell>
            <ch-tabular-grid-cell class="tabular-grid-cell">
              {`${formatDate(obj.modifiedOn)}`}
            </ch-tabular-grid-cell>
          </ch-tabular-grid-row>
        ))}
      </ch-tabular-grid>
    );
  };

  render(): void {
    const buttonRemoveSelectedDisabled = !(this.selectedObjectsIds.length > 0);
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
        <section class="section">
          <header class="header field-group dialog-header header header-two-cols spacing-body-block-start spacing-body-inline">
            <div class="buttons-spacer">
              <button
                // remove selected button
                class="button-secondary"
                onClick={
                  !buttonRemoveSelectedDisabled &&
                  this.#removeSelectedObjectsHandler
                }
                part="remove"
                disabled={buttonRemoveSelectedDisabled}
              >
                {this.#_componentLocale.btnRemoveSelectedObjects}
              </button>

              <button
                // add button
                class="button-secondary button-icon-and-text"
                onClick={this.#getObjectsHandler}
                part="add"
              >
                <ch-image class="icon-m" src={GEMINI_TOOLS_ADD_ICON}></ch-image>
                {this.#_componentLocale.btnAddObject}
              </button>
            </div>
          </header>
          {this.#renderObjects()}
          <footer class="dialog-footer dialog-footer-space-between spacing-body-block-end spacing-body-inline">
            <button
              // remove all button
              class="button-tertiary"
              onClick={this.#removeAllHandler}
              disabled={!(this.selection.length > 0)}
              part="clear"
            >
              {this.#_componentLocale.footer.btnRemoveAll}
            </button>

            <div class="buttons-spacer">
              <button
                // cancel button
                class="button-secondary"
                onClick={this.#cancelHandler}
                part="cancel"
              >
                {this.#_componentLocale.footer.btnCancel}
              </button>

              <button
                // confirm button
                class="button-primary"
                onClick={this.#confirmHandler}
                part="confirm"
              >
                {this.#_componentLocale.footer.btnConfirm}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type AddCallback = () => Promise<ObjectData[]>;
export type CancelCallback = () => Promise<void>;
export type ConfirmCallback = (ids: string[]) => Promise<void>;

export interface ObjectData {
  id: string;
  typeIcon: string;
  name: string;
  type: string;
  description: string;
  modifiedOn: Date;
}
